import { SbBlokData, storyblokEditable } from '@storyblok/react/rsc'
import { FC, JSX } from 'react'
import { MARK_LINK, NODE_LI, NODE_PARAGRAPH, render } from 'storyblok-rich-text-react-renderer'

import Gridpanel from '../../02-molecules/Gridpanel/Blok'
import AccordionItem from '../Accordionitem/Blok'
import Button from '../Button/Blok'
import IFrame from '../IFrame/Blok'
import ImageHolder from '../ImageHolder/Blok'
import { PhnxImageHolder } from '../ImageHolder/ImageHolder'
import Table from '../Table/Blok'
import VideoHolder from '../VideoHolder/Blok'

interface RichTextBlokProps {
  blok: { content: any } & SbBlokData
}

const RichText: FC<RichTextBlokProps> = ({ blok }) => {
  return (
    <div {...storyblokEditable(blok)} className="richText">
      {render(blok.content, {
        blokResolvers: {
          button: (props: any) => <Button blok={props} />,
          image_holder: (props: any) => <ImageHolder blok={props} />,
          grid_panel: (props: any) => <Gridpanel blok={props} />,
          video_holder: (props: any) => <VideoHolder blok={props} />,
          AccordionItem: (props: any) => <AccordionItem blok={props} />,
          iframe: (props: any) => <IFrame blok={props} />,
          basic_table: (props: any) => <Table blok={props} />,
        },
        defaultBlokResolver: (name, props) => (
          <div>
            <code>Missing blok resolver for blok type {name}.</code>
            <pre>
              <code>{JSON.stringify(props, undefined, 2)}</code>
            </pre>
          </div>
        ),
        nodeResolvers: {
          [NODE_LI]: (children: any) => {
            if (children == null) return <br />
            return <li>{children}</li>
          },
          [NODE_PARAGRAPH]: (children) => {
            if (children == null) return <br />
            if (Array.isArray(children) && children[0]?.type === 'img') {
              return (
                <PhnxImageHolder
                  images={[
                    {
                      src: children[0].props.src,
                      alt: children[0].props.alt,
                      focus: children[0].props.focus,
                    },
                  ]}
                />
              )
            }
            return <p>{children}</p>
          },
          bullet_list: (children) => {
            if (!Array.isArray(children)) return null

            const renderNestedList = (items: any[]): JSX.Element => (
              <ul>
                {items.map((item, index) => {
                  const itemChildren = item.props.children
                  const hasNestedList = itemChildren.some((child: any) => child.type === 'ul')

                  return (
                    <li key={index} className={hasNestedList ? 'flex flex-wrap' : ''}>
                      {itemChildren.map((child: any, childIndex: number) => {
                        switch (child.type) {
                          case 'p':
                            return (
                              <p key={childIndex} className="w-full">
                                {child.props.children}
                              </p>
                            )
                          case 'ul':
                            return renderNestedList(child.props.children)
                          default:
                            return child
                        }
                      })}
                    </li>
                  )
                })}
              </ul>
            )

            return renderNestedList(children)
          },
        },
        markResolvers: {
          [MARK_LINK]: (children, props) => {
            const { href, target, linktype } = props
            return (
              <a
                href={linktype === 'email' ? `mailto:${href}` : href}
                target={target}
                rel={target === '_blank' ? 'noopener noreferrer' : undefined}
              >
                {children}
              </a>
            )
          },
        },
      })}
    </div>
  )
}

export default RichText
